import React, { useRef, useState } from 'react';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { TextPairing } from '@components/TextPairing';
import { AllTheLittleDetailsStatic } from '@sections/wedding-website/AllTheLittleDetailsStatic';
import { Box } from '@components/Box';
import { Text } from '@components/typography';
import { AllTheLittleDetailsSectionContainer } from '@sections/wedding-website/AllTheLittleDetailsStatic/AllTheLittleDetailsStatic.styles';
import {
  SiteToSeeSectionDesktopWrapper,
  VideoContentWrapper,
  SiteToSeeContainer,
  VideoContainer,
  VideoWrapper,
  BgLeftPlaceholderCloudImage,
  FrameLeftPlaceholderCloudImage,
  BgRightPlaceholderCloudImage,
  FrameRightPlaceholderCloudImage,
  SitePlaceholderCloudImage,
  StyledTitle,
  SiteToSeeSectionMobileWrapper,
  SiteToSeeWrapperMobileContainer,
  StyledMobileImageWrapper,
  StyledPlaceholderCloudImage,
  StyledTextPlaceholderCloudImage,
  WhatToSiteContainerMobile,
  ImageWrapper,
  StyledCarousel,
  StyledCarouselPlaceholderCloudImage,
  StyledDotIndicator,
  StyledCarouselDot,
  StyledTextPairing,
  VimeoWrapper,
} from './SiteToSeeNewSection.styles';
import { allTheLittleDetailsData, siteToSeeData } from '@data-ts/components/AllTheLittleDetails';
import { VimeoVideo } from '@components/VimeoVideo';
import { weddingWebsitePageData } from '@data-ts/pages/wedding-website';

const siteToSeeSectionData = weddingWebsitePageData.sections.siteToSee;
const sectionId = 'Site_To_See';

export const SiteToSeeSectionDesktop: React.FC = () => {
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const allTheLittleDetailsContainerId = 'Page_Features';
  const allTheLittleDetailsContainerRef = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState(false);
  const LeftBgFlower = 'wedding-website/site-to-see/bg-flower1.jpg';
  const RightBgFlower = 'wedding-website/site-to-see/bg-flower2.jpg';
  const LeftFrameFlower = 'wedding-website/site-to-see/frame-flower1.jpg';
  const RightFrameFlower = 'wedding-website/site-to-see/frame-flower2.jpg';

  return (
    <SiteToSeeSectionDesktopWrapper id={sectionId} ref={contentWrapperRef} backgroundColor="white" marginTop={200}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
        <div />
      </LazyLoadWrapper>

      <VideoContentWrapper>
        <VideoContainer>
          <BgLeftPlaceholderCloudImage isMainSrcShown={isVisible} src={LeftBgFlower} width="340" height="340" optimization="default" />
          <FrameLeftPlaceholderCloudImage
            isMainSrcShown={isVisible}
            src={LeftFrameFlower}
            width="361"
            height="361"
            optimization="default"
          />
          <VideoWrapper>
            <VimeoWrapper>
              <VimeoVideo title={siteToSeeSectionData.videoTitle} embedUrl={siteToSeeSectionData.videoUrl} background cover loop />
            </VimeoWrapper>
          </VideoWrapper>
          <FrameRightPlaceholderCloudImage
            isMainSrcShown={isVisible}
            src={RightFrameFlower}
            width="345"
            height="345"
            optimization="default"
          />
          <BgRightPlaceholderCloudImage isMainSrcShown={isVisible} src={RightBgFlower} width="340" height="340" optimization="default" />
        </VideoContainer>
      </VideoContentWrapper>

      <SiteToSeeContainer marginTop={11}>
        <SitePlaceholderCloudImage
          isMainSrcShown={isVisible}
          src="wedding-website/site-to-see/site-to-see-text.jpg"
          width="800px"
          height="86px"
          optimization="default"
        />
        <StyledTitle tagName="p" variant="dek24" color="black" textAlign="center" marginTop={10}>
          {siteToSeeData.siteToSeeDescription}
        </StyledTitle>
      </SiteToSeeContainer>

      <AllTheLittleDetailsSectionContainer marginTop={14} ref={allTheLittleDetailsContainerRef} id={allTheLittleDetailsContainerId}>
        <AllTheLittleDetailsStatic />
      </AllTheLittleDetailsSectionContainer>
    </SiteToSeeSectionDesktopWrapper>
  );
};

const mobileSlideCoupleHeight = 320;

export const SiteToSeeSectionMobile: React.FC = _props => {
  const [isScrolledIntoView, setIsScrolledIntoView] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const sectionIdMobile = 'Site_to_See_Mobile';
  const sectionRef = useRef<HTMLDivElement>(null);

  return (
    <SiteToSeeSectionMobileWrapper ref={sectionRef} id={sectionIdMobile}>
      <LazyLoadWrapper
        beforeLoad={() => {
          setIsScrolledIntoView(true);
        }}
      >
        <div />
      </LazyLoadWrapper>
      <SiteToSeeWrapperMobileContainer backgroundColor="white" paddingTop={11} paddingBottom={11}>
        <StyledPlaceholderCloudImage
          src="wedding-website/site-to-see/site-to-see-img-mobile.jpg"
          alt="Site To See Text"
          isMainSrcShown={isScrolledIntoView}
          width="100%"
          height="117px"
          optimization="default"
        />
        <StyledMobileImageWrapper paddingTop={6}>
          <StyledTextPlaceholderCloudImage
            src="wedding-website/site-to-see/site-to-see-text-mobile.jpg"
            alt="Site To See Text"
            isMainSrcShown={isScrolledIntoView}
            width="100%"
            height="117px"
            optimization="default"
          />
        </StyledMobileImageWrapper>
        <WhatToSiteContainerMobile>
          <Text variant="dek24" tagName="p" textAlign="center" color="neutra6">
            {siteToSeeData.siteToSeeDescriptionMobile}
          </Text>
        </WhatToSiteContainerMobile>
      </SiteToSeeWrapperMobileContainer>
      <Box backgroundColor="humana1Light" paddingTop={8}>
        <StyledTextPairing
          title={allTheLittleDetailsData.mobileTextBlock.title}
          titleColor="neutra6"
          titleVariant="hed40"
          titleMaxWidth="240px"
          titleTagName="h2"
          body={allTheLittleDetailsData.mobileTextBlock.body}
          bodyColor="neutra5"
          bodyVariant="featureCopy17"
          bodyMaxWidth="270px"
          paddingX={[10]}
          paddingTop={[8]}
        />
      </Box>
      <Box backgroundColor="humana1Light">
        <ImageWrapper>
          <StyledCarousel
            selectedSlide={selectedSlide + 1}
            renderBottomCenterControls={null}
            autoplay={false}
            slideIndex={selectedSlide}
            wrapAround={false}
            easing="easeSinInOut"
            withoutControls={true}
            beforeSlide={(curIndex, newIndex) => {
              return setSelectedSlide(newIndex);
            }}
          >
            {allTheLittleDetailsData.blocks.map((slide, index) => (
              <StyledCarouselPlaceholderCloudImage
                key={index}
                src={slide.image}
                alt={slide.imageAltText}
                isMainSrcShown={isScrolledIntoView}
                resizeHeight={mobileSlideCoupleHeight * 2}
                height={mobileSlideCoupleHeight}
                optimization="default"
              />
            ))}
          </StyledCarousel>

          <StyledCarousel
            selectedSlide={selectedSlide + 1}
            renderBottomCenterControls={null}
            defaultControlsConfig={{
              nextButtonStyle: {
                opacity: '0',
                width: '10vw',
                height: '25vh',
                marginBottom: '50px',
              },
              prevButtonStyle: {
                opacity: '0',
                width: '10vw',
                height: '25vh',
                marginBottom: '50px',
              },
            }}
            autoplay={false}
            slideIndex={selectedSlide}
            cellAlign={'center'}
            slidesToShow={1}
            wrapAround={false}
            easing="easeSinInOut"
            beforeSlide={(curIndex, newIndex) => {
              return setSelectedSlide(newIndex);
            }}
          >
            {allTheLittleDetailsData.blocks.map((slide, index) => (
              <div key={index} style={{ height: '100%' }}>
                <Box paddingX={0}>
                  <TextPairing
                    icon={slide.icon}
                    iconSize={48}
                    title={slide.title}
                    titleVariant="sectionEyeBrow24"
                    titleColor="neutra6"
                    body={slide.body}
                    bodyMaxWidth="263px"
                    bodyVariant="dek24"
                    bodyColor="neutra5"
                    iconColor="accentBlue4"
                  />
                </Box>
              </div>
            ))}
          </StyledCarousel>
          <StyledDotIndicator>
            {allTheLittleDetailsData.blocks.map((block, index) => (
              <StyledCarouselDot key={index} isActive={selectedSlide === index} onClick={() => setSelectedSlide(index)} />
            ))}
          </StyledDotIndicator>
        </ImageWrapper>
      </Box>
    </SiteToSeeSectionMobileWrapper>
  );
};

export const SiteToSeeNewSection: React.FC<{}> = props => {
  return (
    <>
      <SiteToSeeSectionMobile />
      <SiteToSeeSectionDesktop />
    </>
  );
};

export default SiteToSeeNewSection;
